// Client for the FAQ API (api/faq.php) — admin-manageable question/answer
// entries for the FAQ page.

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export interface FaqItem {
  id: number;
  question: string;
  category: string;
  answer: string[];
  note_text: string | null;
  link: string;
  button_text: string;
  confirm_leave: 0 | 1;
  sort_order: number;
}

export interface FaqItemInput {
  question: string;
  category: string;
  answer: string[];
  note_text: string | null;
  link: string;
  button_text: string;
  confirm_leave: boolean;
  sort_order: number;
}

export async function fetchFaqItems(bustCache = false): Promise<FaqItem[]> {
  // bustCache: see the matching comment on fetchRooms() in roomsApi.ts —
  // the admin panel needs to see its own writes reflected immediately
  // rather than the 60s cached response this endpoint sends for the guest
  // FAQ page.
  const qs = bustCache ? `&v=${Date.now()}` : '';
  const res = await fetch(`${API_BASE}/faq.php?action=list${qs}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load FAQ');
  return data.items;
}

export async function addFaqItem(input: FaqItemInput, csrfToken: string): Promise<{ success: boolean; id?: number; message?: string }> {
  const res = await fetch(`${API_BASE}/faq.php?action=add`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function updateFaqItem(id: number, input: FaqItemInput, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/faq.php?action=update`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function deleteFaqItem(id: number, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/faq.php?action=delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

// The backend orders rows by `category, sort_order` — alphabetical category
// order doesn't read well (e.g. "Amenities" before "Booking"), so this fixed
// sequence defines the intended reading order. Categories not listed here
// (future admin-added ones) sort after these, in first-appearance order.
export const FAQ_CATEGORY_ORDER = [
  'Property & Location',
  'Booking & Cancellation',
  'Check-in & Check-out',
  'Property Access & Keys',
  'Guests & Visitors',
  'Amenities & Services',
  'House Rules',
  'Safety & Security',
];

/** Groups FAQ items by category, ordering the groups per FAQ_CATEGORY_ORDER
 * (unknown categories appended at the end, in first-appearance order). */
export function groupFaqByCategory(items: FaqItem[]): [string, FaqItem[]][] {
  const groups = new Map<string, FaqItem[]>();
  for (const item of items) {
    const list = groups.get(item.category);
    if (list) list.push(item);
    else groups.set(item.category, [item]);
  }
  return Array.from(groups.entries()).sort(([a], [b]) => {
    const ai = FAQ_CATEGORY_ORDER.indexOf(a);
    const bi = FAQ_CATEGORY_ORDER.indexOf(b);
    if (ai === -1 && bi === -1) return 0;
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });
}
